import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Component, Suspense, lazy, useLayoutEffect, useMemo, useSyncExternalStore, type ErrorInfo, type ReactNode } from 'react';
import { App as AntApp, Spin } from 'antd';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/useAuth';
import { ProblemPickerProvider } from '@/contexts/ProblemPickerContext';
import { PrivateRoute } from '@/components/PrivateRoute';
import { RoleRoute } from '@/components/RoleRoute';
import {
  DEVELOPER_WORKBENCH_ROLES,
  getPlatformPostLoginRoute,
} from '@/utils/access';
import {
  clearActiveContour,
  readActiveContour,
  resolveAllowedEntryContourIds,
  resolveActiveContour,
  resolveEntryContourFallback,
  selectActiveContour,
} from '@/utils/activeContour';
import { KPI_ADMIN_ROUTE_PATH, KPI_ANALYTICS_ROUTE_PATH } from '@/utils/kpiAdminAccess';
import { resolveKpiWorkingRouteAccess } from '@/utils/kpiWorkingRoute';
import { getAuthenticatedEntryRoute } from '@/utils/mobileShell';
import { isTechnicalContour } from '@/utils/technicalContour';
import { isWasteVerticalHost, WASTE_VERTICAL_ROUTE } from '@/utils/verticalHost';
import { getPlatformRootEntryKind, isPublicPlatformEntryHostname } from '@/utils/publicEntryHost';
import { resolveOwnerAssistantAccess } from '@/utils/ownerAssistantAccess';
import './App.css';

const AuthenticatedEntryRedirect: React.FC = () => {
  const { user, accessPolicy, entryContext } = useAuth();
  if (!entryContext) return <div className="appRouteLoader"><Spin size="large" /></div>;
  return <Navigate to={getPlatformPostLoginRoute(
    user,
    accessPolicy,
    entryContext,
    getAuthenticatedEntryRoute(),
    typeof window !== 'undefined' ? window.location.hostname : undefined,
  )} replace />;
};
const LazyLoginPage = lazy(() => import('@/pages/LoginPage').then((mod) => ({ default: mod.LoginPage })));
const LazyOperatorLayout = lazy(() => import('@/components/OperatorLayout').then((mod) => ({ default: mod.OperatorLayout })));
const LazyLandingPage = lazy(() => import('@/pages/LandingPage').then((mod) => ({ default: mod.LandingPage })));
const LazyAccessPendingPage = lazy(() => import('@/pages/AccessPendingPage').then((mod) => ({ default: mod.AccessPendingPage })));
const LazyContourSelectPage = lazy(() => import('@/pages/ContourSelectPage').then((mod) => ({ default: mod.ContourSelectPage })));
const LazyPlatformAdminPage = lazy(() => import('@/pages/PlatformAdminPage').then((mod) => ({ default: mod.PlatformAdminPage })));
const LazyDevTestPage = lazy(() => import('@/pages/DevTestPage').then((mod) => ({ default: mod.DevTestPage })));
const LazyMaxFeedbackMiniAppPage = lazy(() => import('@/pages/MaxFeedbackMiniAppPage').then((mod) => ({ default: mod.MaxFeedbackMiniAppPage })));
const LazyTechnicalContourBoundaryPage = lazy(() => import('@/pages/TechnicalContourBoundaryPage').then((mod) => ({ default: mod.TechnicalContourBoundaryPage })));
const LazyDashboardPage = lazy(() => import('@/pages/DashboardPage').then((mod) => ({ default: mod.DashboardPage })));
const LazyMessagesPage = lazy(() => import('@/pages/MessagesPage').then((mod) => ({ default: mod.MessagesPage })));
const LazyTicketsPage = lazy(() => import('@/pages/TicketsPage').then((mod) => ({ default: mod.TicketsPage })));
const LazyMapPage = lazy(() => import('@/pages/MapPage').then((mod) => ({ default: mod.MapPage })));
const LazyHeatmapLabPage = lazy(() => import('@/pages/HeatmapLabPage').then((mod) => ({ default: mod.HeatmapLabPage })));
const LazyRolesPage = lazy(() => import('@/pages/RolesPage').then((mod) => ({ default: mod.RolesPage })));
const LazySourcesPage = lazy(() => import('@/pages/SourcesPage').then((mod) => ({ default: mod.SourcesPage })));
const LazyTenantSettingsPage = lazy(() => import('@/pages/TenantSettingsPage').then((mod) => ({ default: mod.TenantSettingsPage })));
const LazyGovernanceSettingsPage = lazy(() => import('@/pages/GovernanceSettingsPage').then((mod) => ({ default: mod.GovernanceSettingsPage })));
const LazyTenantCommunicationSettingsPage = lazy(() => import('@/pages/TenantCommunicationSettingsPage').then((mod) => ({ default: mod.TenantCommunicationSettingsPage })));
const LazyHelpdeskPage = lazy(() => import('@/pages/HelpdeskPage').then((mod) => ({ default: mod.HelpdeskPage })));
const LazySystemTasksPage = lazy(() => import('@/pages/SystemTasksPage').then((mod) => ({ default: mod.SystemTasksPage })));
const LazyLlmWorkbenchPage = lazy(() => import('@/pages/LlmWorkbenchPage').then((mod) => ({ default: mod.LlmWorkbenchPage })));
const LazyKpiAdminPage = lazy(() => import('@/pages/KpiAdminPage').then((mod) => ({ default: mod.KpiAdminPage })));
const LazyConstructorsPage = lazy(() => import('@/pages/ConstructorsPage').then((mod) => ({ default: mod.ConstructorsPage })));
const LazyOwnerAssistantPage = lazy(() => import('@/pages/OwnerAssistantPage').then((mod) => ({ default: mod.OwnerAssistantPage })));
const LazyProblemPickerChrome = lazy(() => import('@/components/helpdesk/ProblemPickerChrome').then((mod) => ({ default: mod.ProblemPickerChrome })));

const PROBLEM_PICKER_HIDDEN_ROUTES = new Set([
  '/',
  '/login',
  '/access-pending',
  '/dev/test',
  '/feedback/max',
  '/max/feedback',
]);

export const AuthenticatedProblemPickerChrome: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const mgShadowFeatureEnabled = import.meta.env.VITE_FEATURE_MG_SHADOW_GLOBAL_HELPDESK === '1';
  if (
    !mgShadowFeatureEnabled
    || loading
    || !isAuthenticated
    || user?.must_change_password
    || PROBLEM_PICKER_HIDDEN_ROUTES.has(location.pathname)
  ) return null;
  return (
    <Suspense fallback={null}>
      <LazyProblemPickerChrome />
    </Suspense>
  );
};

const TenantSettingsRoute: React.FC = () => {
  const { accessPolicy, user } = useAuth();
  if (
    isWasteVerticalHost()
    || accessPolicy?.access_mode === 'explicit'
    || String(user?.role || '').trim().toLowerCase() === 'department_head'
  ) {
    return withRouteSuspense(<LazyGovernanceSettingsPage />);
  }
  return withRouteSuspense(<LazyTenantSettingsPage />);
};

const RouteLoadingFallback: React.FC = () => (
  <div className="appRouteLoader" aria-live="polite" aria-busy="true">
    <Spin size="large" />
  </div>
);

function isDynamicImportFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(message);
}

class RouteChunkErrorBoundary extends Component<{ children: ReactNode }, { chunkError: boolean }> {
  state = { chunkError: false };

  static getDerivedStateFromError(error: unknown) {
    return { chunkError: isDynamicImportFailure(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (!isDynamicImportFailure(error)) {
      console.error('Route render failed', error, info);
    }
  }

  render() {
    if (!this.state.chunkError) return this.props.children;
    return (
      <div className="appRouteLoader appRouteError" role="alert">
        <strong>Нужно обновить страницу</strong>
        <span>После обновления версии браузер запросил старый модуль. Обновите страницу, чтобы загрузить актуальный интерфейс.</span>
        <button type="button" onClick={() => window.location.reload()}>Обновить</button>
      </div>
    );
  }
}

const withRouteSuspense = (element: React.ReactNode) => (
  <RouteChunkErrorBoundary>
    <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
  </RouteChunkErrorBoundary>
);

const subscribeActiveContour = (onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener('active_contour_changed', onStoreChange);
  return () => window.removeEventListener('active_contour_changed', onStoreChange);
};

export const KpiWorkingRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, accessPolicy, entryContext, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const storedContour = useSyncExternalStore(subscribeActiveContour, readActiveContour, () => null);
  // Every authenticated working route waits for the server entry context.
  // This also replaces a stale foreign session contour for ordinary legacy
  // city accounts before any business-data child can mount.
  const contourBoundRoute = Boolean(user && entryContext);
  const allowedContourIds = useMemo(
    () => resolveAllowedEntryContourIds(entryContext, accessPolicy),
    [accessPolicy, entryContext],
  );
  const fallbackContour = useMemo(
    () => resolveEntryContourFallback(
      entryContext,
      allowedContourIds,
      !isPublicPlatformEntryHostname(),
    ),
    [allowedContourIds, entryContext],
  );
  const contourResolution = useMemo(() => {
    if (!contourBoundRoute || !entryContext) return null;
    return resolveActiveContour({
      search: location.search,
      storedContour,
      allowedContourIds,
      fallbackContour,
    });
  }, [allowedContourIds, contourBoundRoute, entryContext, fallbackContour, location.search, storedContour]);

  useLayoutEffect(() => {
    if (!contourBoundRoute || !entryContext || !contourResolution) return;
    if (contourResolution.selectedContour !== storedContour) {
      if (contourResolution.selectedContour) selectActiveContour(contourResolution.selectedContour);
      else clearActiveContour();
    }
    if (location.search !== contourResolution.sanitizedSearch) {
      navigate({
        pathname: location.pathname,
        search: contourResolution.sanitizedSearch,
        hash: location.hash,
      }, { replace: true });
    }
  }, [contourBoundRoute, contourResolution, entryContext, location.hash, location.pathname, location.search, navigate, storedContour]);

  // Query canonicalization is navigation state, not access authority. Keep the
  // mounted route while ticketId/filter ordering is normalized; block only
  // until the selected contour reaches the validated session store.
  const contourSynchronized = !contourBoundRoute || Boolean(
    contourResolution
    && contourResolution.selectedContour === storedContour,
  );
  const entryContextRequired = Boolean(user);
  if (loading || (entryContextRequired && !entryContext) || !contourSynchronized) {
    return <RouteLoadingFallback />;
  }

  const decision = resolveKpiWorkingRouteAccess({
    user,
    accessPolicy,
    entryContext,
    activeContour: contourResolution?.selectedContour ?? storedContour,
    requiresActiveContour: contourBoundRoute,
  });
  if (!decision.allowed) return <Navigate to={decision.redirectTo} replace />;
  return <>{children}</>;
};

export const OwnerAssistantRoute: React.FC = () => {
  const { entryContext, loading } = useAuth();
  if (loading || !entryContext) return <RouteLoadingFallback />;
  const decision = resolveOwnerAssistantAccess(entryContext);
  if (!decision.allowed) return <Navigate to={decision.redirectTo} replace />;
  return withRouteSuspense(<LazyOwnerAssistantPage />);
};

const PlatformRootRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const entryKind = getPlatformRootEntryKind(undefined, isAuthenticated);

  if (entryKind === 'protected') {
    return (
      <PrivateRoute>
        <AuthenticatedEntryRedirect />
      </PrivateRoute>
    );
  }
  if (loading) return <RouteLoadingFallback />;
  if (entryKind === 'authenticated') {
    return (
      <PrivateRoute>
        <AuthenticatedEntryRedirect />
      </PrivateRoute>
    );
  }
  return withRouteSuspense(<LazyLandingPage />);
};

function App() {
  const technicalContour = isTechnicalContour();
  const wasteVerticalHost = isWasteVerticalHost();
  const technicalBoundary = (requestedSectionLabel: string) => (
    withRouteSuspense(<LazyTechnicalContourBoundaryPage requestedSectionLabel={requestedSectionLabel} />)
  );

  return (
    <AntApp>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ProblemPickerProvider>
            <Routes>
            <Route path="/dev/test" element={withRouteSuspense(<LazyDevTestPage />)} />
            <Route path="/" element={<PlatformRootRoute />} />
            <Route path="/login" element={withRouteSuspense(<LazyLoginPage />)} />
            <Route path="/access-pending" element={withRouteSuspense(<LazyAccessPendingPage />)} />
            <Route
              path="/select-contour"
              element={<PrivateRoute>{withRouteSuspense(<LazyContourSelectPage />)}</PrivateRoute>}
            />
            <Route
              path="/admin/access"
              element={<PrivateRoute>{withRouteSuspense(<LazyPlatformAdminPage />)}</PrivateRoute>}
            />
            <Route
              path="/platform"
              element={<PrivateRoute>{withRouteSuspense(<LazyPlatformAdminPage />)}</PrivateRoute>}
            />
            <Route
              path="/platform/uk"
              element={<PrivateRoute>{withRouteSuspense(<LazyPlatformAdminPage />)}</PrivateRoute>}
            />
            <Route
              path="/platform/users"
              element={<PrivateRoute>{withRouteSuspense(<LazyPlatformAdminPage />)}</PrivateRoute>}
            />
            <Route path="/feedback/max" element={withRouteSuspense(<LazyMaxFeedbackMiniAppPage />)} />
            <Route path="/max/feedback" element={<Navigate to="/feedback/max" replace />} />
            <Route
              path="/owner/assistant"
              element={<PrivateRoute><OwnerAssistantRoute /></PrivateRoute>}
            />
            <Route
              path={`/${KPI_ADMIN_ROUTE_PATH}`}
              element={
                <PrivateRoute>
                  <KpiWorkingRoute>
                    {technicalContour
                      ? technicalBoundary('Пульс города')
                      : withRouteSuspense(<LazyKpiAdminPage />)}
                  </KpiWorkingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path={KPI_ANALYTICS_ROUTE_PATH}
              element={
                <PrivateRoute>
                  <KpiWorkingRoute>
                    {technicalContour
                      ? technicalBoundary('KPI-аналитика')
                      : withRouteSuspense(<LazyKpiAdminPage />)}
                  </KpiWorkingRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="/export"
              element={
                wasteVerticalHost
                  ? <Navigate to={WASTE_VERTICAL_ROUTE} replace />
                  : technicalContour
                    ? <PrivateRoute>{technicalBoundary('Экспорт')}</PrivateRoute>
                    : <Navigate to="/new-analytics#export" replace />
              }
            />
            <Route
              path="/analytic"
              element={
                wasteVerticalHost
                  ? <Navigate to={WASTE_VERTICAL_ROUTE} replace />
                  : technicalContour
                    ? <PrivateRoute>{technicalBoundary('Аналитика')}</PrivateRoute>
                    : <Navigate to="/new-analytics#research" replace />
              }
            />
            <Route
              path="/analytics"
              element={
                wasteVerticalHost
                  ? <Navigate to={WASTE_VERTICAL_ROUTE} replace />
                  : technicalContour
                    ? <PrivateRoute>{technicalBoundary('Аналитика')}</PrivateRoute>
                    : <Navigate to="/new-analytics#research" replace />
              }
            />
            <Route
              element={
                <PrivateRoute>
                  {withRouteSuspense(<LazyOperatorLayout />)}
                </PrivateRoute>
              }
            >
              <Route path="dashboard" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Дашборд') : withRouteSuspense(<LazyDashboardPage />)} />
              <Route path="messages" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Сообщения') : withRouteSuspense(<LazyMessagesPage />)} />
              <Route path="tickets" element={technicalContour ? technicalBoundary('Задачи') : withRouteSuspense(<LazyTicketsPage />)} />
              <Route path="instances" element={<Navigate to="/dashboard" replace />} />
              <Route path="errors" element={<Navigate to="/dashboard" replace />} />
              <Route path="monitor" element={<Navigate to="/dashboard" replace />} />
              <Route path="map" element={technicalContour ? technicalBoundary('Карта') : withRouteSuspense(<LazyMapPage />)} />
              <Route path="heatmap-lab" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Лаб теплокарт') : withRouteSuspense(<LazyHeatmapLabPage />)} />
              <Route path="docs" element={<Navigate to="/dashboard" replace />} />
              <Route path="export" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Экспорт') : <Navigate to="/new-analytics#export" replace />} />
              <Route path="analytic" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Аналитика') : <Navigate to="/new-analytics#research" replace />} />
              <Route path="analytics" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Аналитика') : <Navigate to="/new-analytics#research" replace />} />
              <Route path="verticals/waste-removal" element={<Navigate to={WASTE_VERTICAL_ROUTE} replace />} />
              <Route
                path="constructors"
                element={
                  wasteVerticalHost ? (
                    <Navigate to={WASTE_VERTICAL_ROUTE} replace />
                  ) : technicalContour ? (
                    technicalBoundary('Конструктор')
                  ) : (
                    <RoleRoute roles={Array.from(DEVELOPER_WORKBENCH_ROLES)} fallbackPath="/dashboard">
                      {withRouteSuspense(<LazyConstructorsPage />)}
                    </RoleRoute>
                  )
                }
              />
              <Route path="sources" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Источники') : withRouteSuspense(<LazySourcesPage />)} />
              <Route path="max-feedback" element={<Navigate to="/dashboard" replace />} />
              <Route path="roles" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Роли') : withRouteSuspense(<LazyRolesPage />)} />
              <Route path="tenant-settings" element={technicalContour ? technicalBoundary('Настройки города') : <TenantSettingsRoute />} />
              <Route path="tenant-communications" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : technicalContour ? technicalBoundary('Настройки коммуникаций') : withRouteSuspense(<LazyTenantCommunicationSettingsPage />)} />
              <Route path="helpdesk" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : withRouteSuspense(<LazyHelpdeskPage />)} />
              <Route path="helpdesk/system-tasks" element={wasteVerticalHost ? <Navigate to={WASTE_VERTICAL_ROUTE} replace /> : withRouteSuspense(<LazySystemTasksPage />)} />
              <Route
                path="dev/llm-compare"
                element={
                  wasteVerticalHost ? (
                    <Navigate to={WASTE_VERTICAL_ROUTE} replace />
                  ) : technicalContour ? (
                    technicalBoundary('LLM Workbench')
                  ) : (
                    <RoleRoute roles={Array.from(DEVELOPER_WORKBENCH_ROLES)}>
                      {withRouteSuspense(<LazyLlmWorkbenchPage />)}
                    </RoleRoute>
                  )
                }
              />
              <Route path="users" element={<Navigate to="/roles" replace />} />
              <Route path="telegram-moderation" element={<Navigate to="/dashboard" replace />} />
              <Route path="parser" element={<Navigate to={wasteVerticalHost ? "/tenant-settings" : "/tenant-settings?tab=infofield"} replace />} />
              <Route path="infofield" element={<Navigate to={wasteVerticalHost ? "/tenant-settings" : "/tenant-settings?tab=infofield"} replace />} />
              <Route path="telegram-roles" element={<Navigate to="/roles" replace />} />
              <Route path={KPI_ADMIN_ROUTE_PATH} element={<Navigate to={`/${KPI_ADMIN_ROUTE_PATH}`} replace />} />
              <Route path="admin/db" element={<Navigate to="/dashboard" replace />} />
              <Route path="admin/fleet" element={<Navigate to="/dashboard" replace />} />
              <Route path="ops" element={<Navigate to="/dashboard" replace />} />
              <Route path="admin/import" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<AuthenticatedEntryRedirect />} />
            </Route>
            </Routes>
            <AuthenticatedProblemPickerChrome />
          </ProblemPickerProvider>
        </AuthProvider>
      </BrowserRouter>
    </AntApp>
  );
}

export default App;
